import {AUDIT_TAGS, AggregationTypes} from '../constants.js';
import {CartoAPIError} from '../api/index.js';
import type {Filters} from '../types.js';
import {InvalidColumnError} from '../utils.js';
import {WidgetQuerySource, WidgetTableSource} from '../widget-sources/index.js';
import {composeDatasetFilters} from './control-filters.js';
import {
  isExceptSelection,
  isSourceBoundTo,
  singleDefaultSelection,
  type ControlParameter,
} from './controls.js';
import {shapeControlValues} from './control-values.js';
import type {Dataset} from './types.js';
import type {_FetchMapContext} from './fetch-map.js';
import {_getSpatialSourceProps} from './source.js';

const needsFirstValue = (parameter: ControlParameter) =>
  parameter.selectionMode === 'single' &&
  singleDefaultSelection(parameter) === undefined &&
  (parameter.selection === undefined ||
    parameter.selection === 'all' ||
    isExceptSelection(parameter.selection) ||
    (Array.isArray(parameter.selection) && parameter.selection.length === 0));

function createControlWidgetSource(
  dataset: Dataset,
  context: _FetchMapContext
) {
  const props = {
    accessToken: context.accessToken!,
    connectionName: dataset.connectionName,
    apiBaseUrl: context.apiBaseUrl,
    headers: context.headers,
    maxLengthURL: context.maxLengthURL,
    ..._getSpatialSourceProps(dataset.geoColumn),
    ...(context.mapId && {tags: {[AUDIT_TAGS.mapId]: context.mapId}}),
  };
  return dataset.type === 'query'
    ? new WidgetQuerySource({
        ...props,
        sqlQuery: dataset.source,
        queryParameters: dataset.queryParameters,
      })
    : new WidgetTableSource({...props, tableName: dataset.source});
}

async function resolveDataset(
  dataset: Dataset,
  parameters: ControlParameter[],
  context: _FetchMapContext
) {
  const working = [...parameters];
  const indexes = working.flatMap((parameter, index) =>
    isSourceBoundTo(parameter, dataset) && parameter.type === 'SourceCategory'
      ? [index]
      : []
  );
  const pending = indexes.filter((index) => needsFirstValue(working[index]));
  if (pending.length === 0) {
    return [];
  }
  const narrowExcept = (index: number) =>
    composeDatasetFilters(
      {
        sqlParameters: indexes
          .filter((other) => other !== index)
          .map((other) => working[other]),
      },
      dataset,
      true
    );
  const widgetSource = createControlWidgetSource(dataset, context);
  const fetchList = async (control: ControlParameter, filters?: Filters) => {
    try {
      const categories = await widgetSource.getCategories({
        column: control.binding!.column,
        operation: AggregationTypes.Count,
        filters: filters ?? {},
        filterOwner: control.id,
        orderBy: control.orderBy,
      });
      return shapeControlValues(categories);
    } catch (error) {
      if (error instanceof InvalidColumnError) {
        return shapeControlValues([]);
      }
      throw new CartoAPIError(error as Error, {
        requestType: 'SQL',
        connection: dataset.connectionName,
        type: dataset.type,
        source: dataset.source,
      });
    }
  };
  for (const index of pending) {
    const {values} = await fetchList(working[index], narrowExcept(index));
    if (values.length > 0) {
      working[index] = {...working[index], selection: [values[0]]};
    }
  }
  return pending.flatMap((index) =>
    working[index].selection === parameters[index].selection
      ? []
      : [{index, selection: working[index].selection}]
  );
}

export async function resolveFirstValueControls<
  T extends {datasets: Dataset[]; keplerMapConfig: any},
>(map: T, context: _FetchMapContext): Promise<T> {
  const parameters: unknown = map.keplerMapConfig?.config?.sqlParameters;
  if (!Array.isArray(parameters)) {
    return map;
  }
  const targets = map.datasets.filter(
    ({type}) => type === 'table' || type === 'query'
  );
  const patches = (
    await Promise.all(
      targets.map((dataset) => resolveDataset(dataset, parameters, context))
    )
  ).flat();
  if (patches.length === 0) {
    return map;
  }
  const sqlParameters = [...parameters];
  for (const {index, selection} of patches) {
    sqlParameters[index] = {...sqlParameters[index], selection};
  }
  return {
    ...map,
    keplerMapConfig: {
      ...map.keplerMapConfig,
      config: {...map.keplerMapConfig.config, sqlParameters},
    },
  };
}
